import React from "react";
import { AgGridReact, Button, Flex, Grid, InfoBox, Link, Text } from "@jsq/ds/core";
import { ButtonWrapper } from "../common/ButtonWrapper";
import { NameRenderer } from "../common/NameRenderer";
import { OwnerOrControllerSummaryListActions } from "../common/OwnerOrControllerSummaryListActions";
import { RowItem, TypedAGGridCellRenderParams } from "../common/types";
import { boldText, infoBoxStyle, titleStyle } from "./style_vanilla.css";
import { BeneficialOwnerSummaryListProps } from "./types";

export const BeneficialOwnerSummaryList: React.FC<BeneficialOwnerSummaryListProps> = ({
  entityName,
  childOwnershipThreshold,
  isJoinAccount,
  owners,
  onAddOwner,
  onCompleted,
  onBack,
  onEdit,
  onDelete,
  readOnly,
}: BeneficialOwnerSummaryListProps) => {
  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    suppressCellFocus: true,
    domLayout: "autoHeight",
    rowHeight: 56,
  };

  const isCompleteButtonDisabled = isJoinAccount && owners.length < 2;
  const completeButtonTooltip = isCompleteButtonDisabled
    ? "A minimum of two owners is required to complete this section."
    : undefined;

  const colDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "name",
      headerName: "Name",
      flex: readOnly ? 2 : 1,
      cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
        <NameRenderer params={params} />
      ),
    },
    {
      field: "relationship",
      headerName: "Relationship",
      flex: readOnly ? 2 : 1,
    },
    ...(!isJoinAccount
      ? [
          {
            field: "ownershipPercentage",
            headerName: "Ownership percentage",
            flex: 1,
            cellStyle: { textAlign: "right" }, // Align content to the right
            valueFormatter: (params: { value: string | number }) => `${params.value}%`, // Format the value as `${value}%`
          },
        ]
      : []),
    ...(!readOnly
      ? [
          {
            maxWidth: 100,
            resizable: false,
            suppressHeaderMenuButton: true,
            suppressMovable: true,
            sortable: false,
            field: "actions",
            headerName: "",
            cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
              <OwnerOrControllerSummaryListActions
                params={params}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Flex flexDirection="column">
      {entityName && (
        <Link
          to=""
          data-testid="beneficial-owner-selection-back-link"
          variant="muted"
          icon="arrow-left"
          iconPosition="left"
          align="left"
          onClick={onBack}
        >
          {entityName}
        </Link>
      )}
      <Text variant="h3" className={titleStyle}>
        {`Owners of ${entityName}`}
      </Text>
      {isJoinAccount ? (
        <InfoBox variant="basic" title="Owner" className={infoBoxStyle}>
          An owner is any individual that co-owns a joint account. A minimum of two owners is
          required to establish a joint account.
        </InfoBox>
      ) : (
        <InfoBox variant="basic" title="Owner" className={infoBoxStyle}>
          An owner is an individual or entity who owns, directly or indirectly,{" "}
          <Text asSpan className={boldText}>
            {Math.floor(childOwnershipThreshold || 0)}% or more
          </Text>
          {` of the equity interests of ${entityName}. Select from existing individuals or entities or create a new owner`}
        </InfoBox>
      )}

      <Flex flexDirection="column" gap={20}>
        <Flex>
          <Button
            icon="plus"
            onClick={() => {
              onAddOwner();
            }}
            disabled={readOnly}
          >
            Add owner
          </Button>
        </Flex>
        {owners.length > 0 && (
          <Grid gridOptions={gridOptions} rowData={owners} columnDefs={colDefs}></Grid>
        )}
        <Flex justifyContent="flex-end" gap={8}>
          <Button
            variant="neutral"
            onClick={() => {
              onBack();
            }}
          >
            Back
          </Button>
          <ButtonWrapper
            tooltip={completeButtonTooltip}
            id="complete-button-help-indicator"
            disabled={isCompleteButtonDisabled}
          >
            <Button
              onClick={() => {
                void onCompleted();
              }}
              disabled={isCompleteButtonDisabled}
            >
              Complete
            </Button>
          </ButtonWrapper>
        </Flex>
      </Flex>
    </Flex>
  );
};
